import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import {
  Download,
  ChevronLeft,
  ChevronRight,
  Palette,
  Copy,
  Loader2,
  Maximize2,
  Minimize2,
  User,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { apiRequest } from "@/lib/queryClient";
import CarouselSlide from "./carousel-slide";
import {
  CAROUSEL_TEMPLATES,
  PREVIEW_SCALE,
  SLIDE_WIDTH,
  SLIDE_HEIGHT,
  type CarouselTemplate,
} from "./carousel-templates";
import type { ParsedCarousel } from "./carousel-parser";

interface BrandAssetSummary {
  id: string;
  name: string;
  fileUrl: string;
  fileType: string;
}

interface CarouselPreviewProps {
  carousel: ParsedCarousel;
}

export default function CarouselPreview({ carousel }: CarouselPreviewProps) {
  const carouselStyleId = useStore((s) => s.carouselStyleId);
  const profileAssetId = useStore((s) => s.carouselProfileAssetId);
  const profileName = useStore((s) => s.carouselProfileName);
  const setProfileAssetId = useStore((s) => s.setCarouselProfileAssetId);
  const setProfileName = useStore((s) => s.setCarouselProfileName);
  const [showProfilePicker, setShowProfilePicker] = useState(false);

  const { data: brandAssets = [] } = useQuery<BrandAssetSummary[]>({
    queryKey: ["/api/carousel-creator/brand-assets"],
    queryFn: () => apiRequest("GET", "/api/carousel-creator/brand-assets"),
  });

  const profileAsset = useMemo(
    () => brandAssets.find((a) => a.id === profileAssetId) ?? null,
    [brandAssets, profileAssetId]
  );

  const defaultTemplate = useMemo(() => {
    if (!carouselStyleId)
      return CAROUSEL_TEMPLATES.find((t) => t.id === "vp-noir-editorial") ?? CAROUSEL_TEMPLATES[0];
    const vpTemplate = CAROUSEL_TEMPLATES.find((t) => t.vpStyleId === carouselStyleId);
    return vpTemplate ?? CAROUSEL_TEMPLATES[0];
  }, [carouselStyleId]);

  const [selectedTemplate, setSelectedTemplate] = useState<CarouselTemplate>(defaultTemplate);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [profileHandle, setProfileHandle] = useState("");
  const [enlarged, setEnlarged] = useState(false);
  const exportContainerRef = useRef<HTMLDivElement>(null);

  // Sync selected template when store carouselStyleId changes
  useEffect(() => {
    setSelectedTemplate(defaultTemplate);
  }, [defaultTemplate]);

  const { slides } = carousel;
  const totalSlides = slides.length;

  const goNext = () => setCurrentSlide((prev) => Math.min(prev + 1, totalSlides - 1));
  const goPrev = () => setCurrentSlide((prev) => Math.max(prev - 1, 0));

  const copyDescription = useCallback(() => {
    if (carousel.postDescription) {
      navigator.clipboard.writeText(carousel.postDescription);
    }
  }, [carousel.postDescription]);

  const exportAllSlides = useCallback(async () => {
    if (!exportContainerRef.current) return;
    setExporting(true);

    try {
      const zip = new JSZip();
      const slideElements = exportContainerRef.current.querySelectorAll(".carousel-slide-render");

      for (let i = 0; i < slideElements.length; i++) {
        const el = slideElements[i] as HTMLElement;
        const canvas = await html2canvas(el, {
          width: SLIDE_WIDTH,
          height: SLIDE_HEIGHT,
          scale: 1,
          useCORS: true,
          backgroundColor: null,
        });

        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((b) => resolve(b!), "image/png", 1.0);
        });

        const slideNum = String(i + 1).padStart(2, "0");
        zip.file(`slajd-${slideNum}.png`, blob);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `karuzela-${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setExporting(false);
    }
  }, []);

  const exportSingleSlide = useCallback(async () => {
    if (!exportContainerRef.current) return;
    setExporting(true);

    try {
      const slideElements = exportContainerRef.current.querySelectorAll(".carousel-slide-render");
      const el = slideElements[currentSlide] as HTMLElement;
      if (!el) return;

      const canvas = await html2canvas(el, {
        width: SLIDE_WIDTH,
        height: SLIDE_HEIGHT,
        scale: 1,
        useCORS: true,
        backgroundColor: null,
      });

      const url = canvas.toDataURL("image/png", 1.0);
      const a = document.createElement("a");
      a.href = url;
      a.download = `slajd-${String(currentSlide + 1).padStart(2, "0")}.png`;
      a.click();
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setExporting(false);
    }
  }, [currentSlide]);

  return (
    <div className="overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40">
            Slajd {currentSlide + 1} / {totalSlides}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEnlarged(!enlarged)}
            className="h-7 px-2 text-xs gap-1"
          >
            {enlarged ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
            {enlarged ? "Mały" : "Duży"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowProfilePicker(!showProfilePicker)}
            className="h-7 px-2 text-xs gap-1"
          >
            <User size={12} />
            Profil
            {profileAsset && (
              <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowTemplates(!showTemplates)}
            className="h-7 px-2 text-xs gap-1"
          >
            <Palette size={12} />
            Szablon
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={exportSingleSlide}
            disabled={exporting}
            className="h-7 px-2 text-xs gap-1"
          >
            <Download size={12} />
            PNG
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={exportAllSlides}
            disabled={exporting}
            className="h-7 px-2 text-xs gap-1"
          >
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Pobierz ZIP
          </Button>
        </div>
      </div>

      {/* Profile picker — pick a brand asset as the avatar shown in the
          top-left of every slide, plus the display name next to it. */}
      {showProfilePicker && (
        <div className="px-4 py-3 border-b border-white/10 space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-white/50 w-16 shrink-0">Imię i nazwisko</label>
            <input
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              placeholder="np. Paweł Kowalski"
              maxLength={60}
              className="flex-1 h-7 px-2 text-xs bg-white/5 border border-white/10 rounded-md text-white/80 placeholder:text-white/30"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-white/50 w-16 shrink-0">@handle</label>
            <input
              type="text"
              value={profileHandle}
              onChange={(e) => setProfileHandle(e.target.value)}
              placeholder="twoj_profil"
              maxLength={40}
              className="flex-1 h-7 px-2 text-xs bg-white/5 border border-white/10 rounded-md text-white/80 placeholder:text-white/30"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] text-white/50">Zdjęcie profilowe</span>
              {profileAsset && (
                <button
                  onClick={() => setProfileAssetId(null)}
                  className="flex items-center gap-1 text-[10px] text-white/40 hover:text-white/70"
                >
                  <X size={10} /> Usuń
                </button>
              )}
            </div>
            {brandAssets.length === 0 ? (
              <div className="text-[10px] text-white/30 py-2 px-2 rounded-md bg-white/[0.03] border border-white/5">
                Brak assetów — najpierw dodaj zdjęcie w zakładce „Assety".
              </div>
            ) : (
              <div className="grid grid-cols-6 gap-1.5">
                {brandAssets.map((asset) => {
                  const active = asset.id === profileAssetId;
                  return (
                    <button
                      key={asset.id}
                      onClick={() => setProfileAssetId(active ? null : asset.id)}
                      title={asset.name}
                      className={`relative aspect-square rounded-md overflow-hidden border-2 transition-all ${
                        active ? "border-emerald-400" : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <img
                        src={asset.fileUrl}
                        alt={asset.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template picker */}
      {showTemplates && (
        <div className="px-4 py-3 border-b border-white/10 space-y-3 max-h-64 overflow-y-auto">
          {/* Layout categories */}
          {[
            { label: "🔥 VP Premium", layouts: ["vp-style"] },
            { label: "Klasyczny", layouts: ["classic"] },
            { label: "Karta", layouts: ["card"] },
            { label: "Bold Numer", layouts: ["bold-number"] },
            { label: "Split", layouts: ["split"] },
            { label: "Editorial", layouts: ["editorial"] },
            { label: "Bąbelek", layouts: ["speech-bubble"] },
          ].map((group) => {
            const groupTemplates = CAROUSEL_TEMPLATES.filter((t) =>
              group.layouts.includes(t.layout)
            );
            if (groupTemplates.length === 0) return null;
            return (
              <div key={group.label}>
                <div className="text-[9px] text-white/30 font-medium uppercase tracking-wider mb-1.5">
                  {group.label}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {groupTemplates.map((tmpl) => {
                    const isGradient = tmpl.background.startsWith("linear-gradient");
                    const active = selectedTemplate.id === tmpl.id;
                    return (
                      <button
                        key={tmpl.id}
                        onClick={() => setSelectedTemplate(tmpl)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs transition-all ${
                          active
                            ? "border-indigo-500 bg-indigo-500/20 text-white"
                            : "border-white/10 text-white/60 hover:border-white/30"
                        }`}
                      >
                        <div
                          className="w-3.5 h-3.5 rounded-sm border border-white/20 flex-shrink-0"
                          style={
                            isGradient
                              ? { background: tmpl.background }
                              : { backgroundColor: tmpl.background }
                          }
                        />
                        <span className="text-[10px]">{tmpl.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Slide preview with navigation */}
      <div className="relative flex items-center justify-center py-6 px-4 select-none">
        {/* Previous */}
        <button
          onClick={goPrev}
          disabled={currentSlide === 0}
          className="absolute left-2 z-10 p-1.5 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 transition-all"
        >
          <ChevronLeft size={18} className="text-white" />
        </button>

        {/* Slide container (scaled preview) */}
        <div
          className="rounded-lg overflow-hidden shadow-2xl border border-white/10 transition-all duration-300"
          style={{
            width: SLIDE_WIDTH * (enlarged ? PREVIEW_SCALE * 2 : PREVIEW_SCALE),
            height: SLIDE_HEIGHT * (enlarged ? PREVIEW_SCALE * 2 : PREVIEW_SCALE),
          }}
        >
          <div
            style={{
              transform: `scale(${enlarged ? PREVIEW_SCALE * 2 : PREVIEW_SCALE})`,
              transformOrigin: "top left",
              width: SLIDE_WIDTH,
              height: SLIDE_HEIGHT,
            }}
          >
            <CarouselSlide
              slide={slides[currentSlide]}
              template={selectedTemplate}
              slideIndex={currentSlide}
              totalSlides={totalSlides}
              profileHandle={profileHandle || undefined}
              profileAvatarUrl={profileAsset?.fileUrl}
              profileName={profileName || undefined}
            />
          </div>
        </div>

        {/* Next */}
        <button
          onClick={goNext}
          disabled={currentSlide === totalSlides - 1}
          className="absolute right-2 z-10 p-1.5 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 transition-all"
        >
          <ChevronRight size={18} className="text-white" />
        </button>
      </div>

      {/* Thumbnail strip */}
      <div className="flex gap-2 px-4 pb-4 overflow-x-auto">
        {slides.map((slide, i) => {
          const isGradient = selectedTemplate.background.startsWith("linear-gradient");
          const thumbBg = isGradient
            ? { background: selectedTemplate.background }
            : { backgroundColor: selectedTemplate.background };
          return (
            <button
              key={slide.number}
              onClick={() => setCurrentSlide(i)}
              className={`shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                i === currentSlide
                  ? "border-indigo-500 scale-105"
                  : "border-white/10 opacity-60 hover:opacity-90"
              }`}
              style={{ width: 48, height: 60 }}
            >
              <div className="w-full h-full flex items-center justify-center" style={thumbBg}>
                <span style={{ color: selectedTemplate.titleColor, fontSize: 10, fontWeight: 700 }}>
                  {i + 1}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Post description */}
      {carousel.postDescription && (
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-white/50">📝 Opis do posta</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={copyDescription}
              className="h-6 px-2 text-xs gap-1"
            >
              <Copy size={10} />
              Kopiuj
            </Button>
          </div>
          <div className="text-xs text-white/70 bg-white/5 rounded-lg p-3 whitespace-pre-wrap max-h-40 overflow-y-auto">
            {carousel.postDescription}
          </div>
        </div>
      )}

      {/* Hidden full-resolution render container for export */}
      <div
        ref={exportContainerRef}
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
        aria-hidden
      >
        {slides.map((slide, i) => (
          <CarouselSlide
            key={slide.number}
            slide={slide}
            template={selectedTemplate}
            slideIndex={i}
            totalSlides={totalSlides}
            profileHandle={profileHandle || undefined}
            profileAvatarUrl={profileAsset?.fileUrl}
            profileName={profileName || undefined}
          />
        ))}
      </div>
    </div>
  );
}
